/**
 * Connect > Agreements manager at /connect/agreements.
 *
 * Cross-partner agreement list, most-actionable-first (sent -> draft ->
 * expired -> declined -> signed -> voided). Filters: status, org. Expiry is
 * highlighted when within 60 days. "New agreement" opens a modal with an org
 * picker. Status transitions inline. DocuSign envelope id shown when present.
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Info } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { SearchableSelect } from '@/components/SearchableSelect';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import { formatDateShort } from '@/lib/dateUtils';
import {
  hasConnectAgreements,
  isNotAvailable,
  agreementStatusLabel,
  agreementStatusRank,
  expiresSoon,
  useConnectAgreements,
  useConnectOrgs,
  usePatchConnectAgreement,
  useCreateConnectAgreement,
  AGREEMENT_STATUS_TONES,
  type ConnectAgreement,
  type ConnectAgreementStatus,
} from '@/lib/useConnect';
import {
  AccessDeniedCard,
  ConnectHeader,
  NotAvailableCard,
  Modal,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './connectShared';

const AGREEMENT_STATUSES: ConnectAgreementStatus[] = [
  'draft', 'sent', 'signed', 'declined', 'voided', 'expired',
];

export function ConnectAgreements() {
  const { user } = useSession();
  // No more read/write levels: holding connect.agreements grants both.
  const canView = hasConnectAgreements(user?.permissions, user?.superAdmin);
  const canWrite = canView;

  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const orgId = searchParams.get('org_id') ?? '';

  const setStr = (k: string, v: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v) next.set(k, v);
        else next.delete(k);
        return next;
      },
      { replace: true },
    );

  const agreementsQ = useConnectAgreements({ status: status || undefined, org_id: orgId || undefined });
  const orgsQ = useConnectOrgs({});
  const patch = usePatchConnectAgreement();
  const [addOpen, setAddOpen] = useState(false);

  const orgOptions = useMemo(
    () => orgsQ.orgs.map((o) => ({ value: String(o.id), label: o.name })),
    [orgsQ.orgs],
  );

  const sorted = useMemo(
    () => [...agreementsQ.agreements].sort((a, b) => agreementStatusRank(a.status) - agreementStatusRank(b.status)),
    [agreementsQ.agreements],
  );

  if (user && !canView) return <AccessDeniedCard />;
  const backendMissing = isNotAvailable(agreementsQ.error);

  const toolbar = (
    <div className="flex flex-wrap gap-3 items-end">
      <div>
        <label htmlFor="status" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Status</label>
        <select
          id="status"
          value={status}
          onChange={(e) => setStr('status', e.target.value)}
          className="min-w-[150px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {AGREEMENT_STATUSES.map((s) => (
            <option key={s} value={s}>{agreementStatusLabel(s)}</option>
          ))}
        </select>
      </div>
      <div>
        <span className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Partner</span>
        <SearchableSelect
          ariaLabel="Partner filter"
          options={orgOptions}
          selected={orgId ? [orgId] : []}
          onChange={(v) => setStr('org_id', v[0] ?? '')}
          multiSelect={false}
          placeholder="Any"
        />
      </div>
      {(status || orgId) && (
        <button type="button" onClick={() => setSearchParams({}, { replace: true })} className={secondaryBtnCls}>
          Reset filters
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ConnectHeader
        title="Agreements"
        subtitle="Partner agreements across every organization. Most-actionable first: sent and draft agreements sit at the top."
        actions={
          <>
            <ToolPinToggle toolSlug="connect-agreements" toolName="Agreements" />
            {canWrite && (
              <button type="button" onClick={() => setAddOpen(true)} className={secondaryBtnCls}>
                <Plus className="h-3 w-3" /> New agreement
              </button>
            )}
          </>
        }
      />

      <div className="mb-4 inline-flex items-start gap-2 rounded-md border border-amp-line bg-amp-cream-warm px-3 py-2 text-xs text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        Live DocuSign send activates once the DocuSign credentials are installed. Until then, agreements are tracked here manually.
      </div>

      {backendMissing ? (
        <NotAvailableCard what="The agreements manager" />
      ) : (
        <DataGridShell
          total={sorted.length}
          loaded={sorted.length}
          countNoun="agreements"
          toolbar={toolbar}
        >
          <table className="min-w-full text-sm">
            <thead className={gridTheadClassName}>
              <tr>
                <th className="px-3 py-2 text-left font-medium">Partner</th>
                <th className="px-3 py-2 text-left font-medium">Title</th>
                <th className="px-3 py-2 text-left font-medium w-[120px]">Type</th>
                <th className="px-3 py-2 text-left font-medium w-[150px]">Status</th>
                <th className="px-3 py-2 text-left font-medium w-[120px]">Sent</th>
                <th className="px-3 py-2 text-left font-medium w-[140px]">Expires</th>
                <th className="px-3 py-2 text-left font-medium w-[160px]">DocuSign</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {agreementsQ.isLoading ? (
                <tr><td colSpan={7} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading agreements...</td></tr>
              ) : agreementsQ.error ? (
                <tr><td colSpan={7} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {agreementsQ.error.message}</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No agreements match this filter.</td></tr>
              ) : (
                sorted.map((a) => (
                  <AgreementRow key={a.id} a={a} canWrite={canWrite} onStatus={(s) => patch.mutate({ id: a.id, body: { status: s } })} />
                ))
              )}
            </tbody>
          </table>
        </DataGridShell>
      )}

      {addOpen && <CrossPartnerAgreementModal orgOptions={orgOptions} onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function AgreementRow({ a, canWrite, onStatus }: { a: ConnectAgreement; canWrite: boolean; onStatus: (s: ConnectAgreementStatus) => void }) {
  const soon = expiresSoon(a.expires_at, 60);
  return (
    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        <Link to={`/connect/partners/${a.org_id}`} className="hover:underline">{a.org_name}</Link>
      </td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{a.title}</td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{a.agreement_type ?? '--'}</td>
      <td className="px-3 py-2">
        {canWrite ? (
          <select
            value={a.status}
            onChange={(e) => onStatus(e.target.value as ConnectAgreementStatus)}
            className="border border-amp-line rounded px-2 py-1 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            {AGREEMENT_STATUSES.map((s) => (
              <option key={s} value={s}>{agreementStatusLabel(s)}</option>
            ))}
          </select>
        ) : (
          <Pill tone={AGREEMENT_STATUS_TONES[a.status]}>{agreementStatusLabel(a.status)}</Pill>
        )}
      </td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(a.sent_at)}</td>
      <td className={`px-3 py-2 whitespace-nowrap ${soon ? 'text-amber-700 font-medium dark:text-amber-300' : 'text-amp-ink-muted dark:text-amp-dark-ink-muted'}`}>
        {formatDateShort(a.expires_at)}{soon && ' (soon)'}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{a.docusign_envelope_id ?? '--'}</td>
    </tr>
  );
}

function CrossPartnerAgreementModal({ orgOptions, onClose }: { orgOptions: { value: string; label: string }[]; onClose: () => void }) {
  const create = useCreateConnectAgreement();
  const [orgId, setOrgId] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState('');
  const [signer, setSigner] = useState('');
  const [status, setStatus] = useState<ConnectAgreementStatus>('draft');
  const [expires, setExpires] = useState('');

  const submit = () => {
    create.mutate(
      {
        org_id: Number(orgId),
        title: title.trim(),
        agreement_type: type.trim() || undefined,
        signer_email: signer.trim() || undefined,
        status,
        expires_at: expires || undefined,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      title="New agreement"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryBtnCls}>Cancel</button>
          <button type="button" onClick={submit} disabled={create.isPending || !orgId || !title.trim()} className={primaryBtnCls}>Create</button>
        </>
      }
    >
      {create.error && <p className="mb-3 text-xs text-red-700 dark:text-red-300">{isNotAvailable(create.error) ? 'Agreements are not available yet.' : create.error.message}</p>}
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Partner</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)} className={inputCls}>
            <option value="">Select a partner...</option>
            {orgOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Type</label>
            <input value={type} onChange={(e) => setType(e.target.value)} className={inputCls} placeholder="terms" />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as ConnectAgreementStatus)} className={inputCls}>
              {AGREEMENT_STATUSES.map((s) => (
                <option key={s} value={s}>{agreementStatusLabel(s)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Signer email</label>
            <input value={signer} onChange={(e) => setSigner(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Expires</label>
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className={inputCls} />
          </div>
        </div>
      </div>
    </Modal>
  );
}
